import { ConfirmDialog } from '@/components/organisms/ConfirmDialog';
import { AlertBanner } from '@/components/molecules/AlertBanner';
import { RetryBlock } from '@/features/settings/common';
import { ProrationRowsSkeleton } from './billing-skeletons';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, toDate } from '@/lib/date';
import { buildProrationRows } from '@/features/billing/plan-helpers';
import { BillingPortalButton } from '@/features/billing/BillingPortalButton';
import { UpgradePromoField } from './UpgradePromoField';
import type { PreviewResponse } from '@/lib/billing';
import type { PlanUpgradeFlow } from './use-plan-upgrade';

type Props = {
  flow: PlanUpgradeFlow;
};

/**
 * Confirm dialog for a self-serve upgrade (P2, spec §8). Renders on top of the
 * `ConfirmDialog` organism; the body swaps by `flow.phase`:
 *   - `confirm`    — proration preview (loading / retry / summary) + any error
 *                    notice from a failed confirm.
 *   - `processing` — a polite "applying" status while the webhook lands.
 *   - `done`       — a success banner once the polled plan flips.
 *   - `timeout`    — a bail-out banner when the webhook didn't land in time, with
 *                    a manual refresh (the confirm button) + a portal hint.
 *
 * Rendered by `UpgradePlansCard` only when `flow.activePlan` is set.
 */
export function PlanUpgradeDialog({ flow }: Props) {
  const plan = flow.activePlan;
  if (!plan) return null;

  const { phase, preview, notice, confirming, promo } = flow;
  const isDone = phase === 'done';
  const isProcessing = phase === 'processing';
  const isTimeout = phase === 'timeout';
  // A held upgrade lock (041007) or a non-purchasable plan (040701) is not
  // retryable in place — block the confirm button, don't just show a message.
  const noticeBlocks = notice?.kind === 'inProgress' || notice?.kind === 'unavailable';

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) flow.closeDialog();
      }}
      title={
        isDone
          ? `Upgraded to ${plan.name}`
          : isTimeout
            ? `Still upgrading to ${plan.name}`
            : `Upgrade to ${plan.name}`
      }
      description={
        isDone
          ? 'Your plan change is confirmed.'
          : isTimeout
            ? "This is taking longer than expected. It'll update shortly."
            : isProcessing
              ? 'Hang tight while we apply your new plan.'
              : `Review the prorated charge for switching to ${plan.name}.`
      }
      confirmLabel={isDone ? 'Done' : isTimeout ? 'Refresh' : 'Confirm upgrade'}
      loadingLabel="Processing…"
      cancelLabel={isDone || isTimeout ? 'Close' : 'Cancel'}
      loading={confirming || isProcessing}
      confirmDisabled={
        phase === 'confirm' &&
        (preview.isLoading || preview.isError || !preview.data || noticeBlocks)
      }
      onConfirm={
        isDone ? flow.closeDialog : isTimeout ? flow.refresh : flow.confirmUpgrade
      }
    >
      {isDone ? (
        <AlertBanner variant="success">
          You&rsquo;re now on {plan.name}. Your new limits are active.
        </AlertBanner>
      ) : isTimeout ? (
        <AlertBanner variant="warning">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>
              Your upgrade to {plan.name} hasn&rsquo;t confirmed yet. Refresh to
              check again, or manage it in the billing portal. It&rsquo;ll update
              shortly.
            </span>
            <BillingPortalButton variant="outline" />
          </span>
        </AlertBanner>
      ) : isProcessing ? (
        <p
          role="status"
          aria-live="polite"
          className="text-sm text-muted-foreground"
        >
          Applying your upgrade to {plan.name}…
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {preview.isLoading && <ProrationRowsSkeleton />}
          {/* A rejected promo code also fails the preview — but it's a field
              error with its own inline copy + "Remove code", not a dead quote,
              so it must not claim the retry path. */}
          {preview.isError && !promo.error && (
            <RetryBlock
              message={getErrorMessage(
                preview.error,
                "We couldn't calculate your upgrade cost right now.",
              )}
              onRetry={preview.refetch}
            />
          )}
          {preview.data && !preview.isError && (
            <ProrationSummary preview={preview.data} />
          )}
          <UpgradePromoField promo={promo} applying={preview.isLoading} />
          {notice && (
            <AlertBanner variant={noticeVariant(notice.kind)}>
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>{notice.message}</span>
                {/* `portal` (041005 PaymentFailed / 041001 DowngradeNotAllowed)
                    is the same dead end as the timeout banner above: the server's
                    message tells the user to go to the portal, so give them the
                    way there. The other kinds are actioned in this dialog. */}
                {notice.kind === 'portal' && <BillingPortalButton variant="outline" />}
              </span>
            </AlertBanner>
          )}
        </div>
      )}
    </ConfirmDialog>
  );
}

/** Banner tone for a confirm-failure notice, by its classified action kind. */
function noticeVariant(
  kind: 'portal' | 'checkout' | 'inProgress' | 'unavailable' | 'retry',
): 'info' | 'warning' | 'error' {
  if (kind === 'portal') return 'info';
  if (kind === 'inProgress') return 'warning';
  return 'error';
}

function ProrationSummary({ preview }: { preview: PreviewResponse }) {
  const rows = buildProrationRows(preview);
  return (
    <div className="flex flex-col gap-2">
      {rows.map((row) => (
        <div
          key={row.label}
          className={cn(
            'flex items-center justify-between text-sm',
            row.emphasis
              ? 'border-t border-[color:var(--ink-100)] pt-2 font-semibold text-[color:var(--ink-900)]'
              : 'text-muted-foreground',
          )}
        >
          <span>{row.label}</span>
          <span className="tabular-nums">{row.value}</span>
        </div>
      ))}
      {preview.hasImmediateCharge && preview.nextBilledAt && (
        <p className="mt-1 text-xs text-muted-foreground">
          Renews on {formatDate(toDate(preview.nextBilledAt))}.
        </p>
      )}
    </div>
  );
}
